import { uuidReplaceRegExp } from "./regexes"

/** The parts of an axios request config needed to tell where a request went. */
export type HttpRequestTarget = {
  url?: string
  baseURL?: string
  method?: string
}

/** Where a request went, reduced to values that group well in Sentry. */
export type HttpEndpoint = {
  /** Upper-cased HTTP method, `UNKNOWN` when absent. */
  method: string
  /** Host (with port) the request went to, `unknown` when the url could not be resolved. */
  host: string
  /** Path with ids, numbers, postal codes and query string stripped (see `normalizeEndpointPath`). */
  path: string
}

/** A rewrite applied to the path before the generic normalisation, e.g. to collapse slugs. */
export type EndpointPathRule = [pattern: RegExp, replacement: string]

const unresolvedOrigin = "http://unresolved.invalid"

/** Whole segments that identify a record rather than an endpoint. */
const idSegmentRegExp = /^(?:\d+|[0-9a-f]{16,}|(?=[a-z0-9_~.-]*\d)[a-z0-9_~.-]{24,})$/i
const postalCodeSegmentRegExp = /^\d{4}\s?[a-z]{2}$/i

/**
 * Describes a request by method, host and endpoint path, so failures can be grouped per endpoint and
 * filtered per host. Mirrors how axios joins `baseURL` and `url`; relative results are resolved against
 * `origin` (the page origin in the browser).
 *
 * For example, `{ baseURL: "https://api.mijnpraktijk.nl/api/v1", url: "/practices/<uuid>?x=1", method: "get" }`
 * becomes `{ method: "GET", host: "api.mijnpraktijk.nl", path: "/api/v1/practices/:id" }`.
 */
export function describeHttpEndpoint(
  { url, baseURL, method }: HttpRequestTarget,
  { origin, rules = [] }: { origin?: string; rules?: EndpointPathRule[] } = {}
): HttpEndpoint {
  const fullUrl = combineUrls(baseURL, url ?? "")
  if (!fullUrl) {
    return { method: method?.toUpperCase() ?? "UNKNOWN", host: "unknown", path: "unknown" }
  }

  let host = "unknown"
  let path = fullUrl.split(/[?#]/)[0]

  try {
    const resolved = new URL(fullUrl, origin ?? unresolvedOrigin)
    if (resolved.origin !== unresolvedOrigin) {
      host = resolved.host
    }
    path = resolved.pathname
  } catch {
    /* unparsable url — keep the raw path */
  }

  return {
    method: method?.toUpperCase() ?? "UNKNOWN",
    host,
    path: normalizeEndpointPath(path, rules) || "unknown",
  }
}

/**
 * Reduces a url path to the endpoint it belongs to: applies the `rules` first, then replaces UUIDs, numeric
 * segments, long tokens and Dutch postal codes with placeholders.
 */
export function normalizeEndpointPath(path: string, rules: EndpointPathRule[] = []): string {
  const ruled = rules.reduce((current, [pattern, replacement]) => current.replace(pattern, replacement), path)

  return ruled
    .replace(uuidReplaceRegExp, ":id")
    .split("/")
    .map((segment) => {
      if (postalCodeSegmentRegExp.test(segment)) {
        return ":postalCode"
      }
      return idSegmentRegExp.test(segment) ? ":id" : segment
    })
    .join("/")
}

/** Joins `baseURL` and `url` the way axios does: an absolute `url` wins, otherwise they are concatenated. */
function combineUrls(baseURL: string | undefined, url: string): string {
  if (!baseURL || /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) {
    return url
  }

  return url ? `${baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}` : baseURL
}
